import axios from "axios";
import { API_URL, API_URL_ANGULAR } from "../utils/Constant";

/**
 * Fetch test qc data
 * @param {*} params { search: string, date_range: string, test_category: string }
 * @param {*} signal
 */
export const fetchQCData = async (params, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/manualqc/`,
      {
        params,
        signal,
      });
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (e) {
    const message = "Failed to fetch QC data";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Update a test's qc status and qc result
 * @param {*} payload {
 * tis_pk: number,
 * qc_result: string,
 * qc_status: string,
 * user_id: number,
 * wo_id_pk: number, // backend needs this for the auto approve report feature
 * generate_retest(optional): "True",
 * contact_customer(optional): "True",
 * mrl_instrument_selection(required for MRL retests): { [string testId]: number | number[] },
 * result(send this if generate_retest or contact_customer exists): {...test_data},
 * }
 */
export const markSampleQC = async (payload) => {
  try {
    const response = await axios.post(`${API_URL}/manualqc/`, payload);
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Failed to update sample";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Update a test's manual qc assigned to
 * @param {*} payload { tis_pk: number, manual_qc_assigned_to: assign_to_id, user_id: number }
 */
export const markManualQcAssignTo = async (payload) => {
  try {
    const response = await axios.post(`${API_URL}/manualqc/`, payload);
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Failed to update assign to";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Fetch a test qc history data
 * @param {*} payload { wo_id_pk: number, genista_id, analysis, date_range: YYYY-MM-DD - YYYY-MM-DD }
 */
export const fetchTestHistoryQCData = async (params, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/check_history/`,
      {
        params,
        signal,
      });
    const apiResponse = await response.data;
    return { success: true, result: apiResponse };
  } catch (e) {
    const message = "Failed to fetch History data";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

export const fetchQCAnalystUsers = async (payload = {}) => {
  try {
    const params = {
      is_active: true,
      role: 15,
      page: 1,
      page_size: 5000,
      search: "",
      ...payload,
    };
    const response = await axios.get(`${API_URL_ANGULAR}/users/`, { params });
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    return { success: false, message: "Failed to load company names for search." };
  }
};

/** 20: Data Ops, 22: Data Ops Lead, 27: Data Ops Intermediate */
export const DATA_OPS_ASSIGNEE_ROLE_IDS = [20, 22, 27];

const serializeUsersQueryParams = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item == null || item === "") {
          return;
        }
        searchParams.append(key, String(item));
      });
      return;
    }
    searchParams.append(key, String(value));
  });
  return searchParams.toString();
};

/**
 * Active users for Data Ops RCA Assign To dropdowns (role_id 20 / 22 / 27).
 * Same /users/ shape as fetchQCAnalystUsers; role_id is repeated for multi-role filter.
 */
export const fetchDataOpsAssigneeUsers = async (payload = {}) => {
  try {
    const params = {
      is_active: true,
      role_id: DATA_OPS_ASSIGNEE_ROLE_IDS,
      page: 1,
      page_size: 5000,
      search: "",
      ...payload,
    };
    const response = await axios.get(`${API_URL_ANGULAR}/users/`, {
      params,
      paramsSerializer: serializeUsersQueryParams,
    });
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    return { success: false, message: "Failed to load Data Ops assignee users." };
  }
};

export const fetchWoStages = async (wo_id) => {
  try {
    const response = await axios.get(`${API_URL_ANGULAR}/stages/?wo_id=${wo_id}`);
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    return { success: false, message: "Failed to load WO stages." };
  }
};

/**
 * Get ML QC Rules
 * @param {{ search?: string, rule_id?: string }} params { search?: string, rule_id?: string }
 * @param {*} signal AbortSignal
 */
export const getMlQcRules = async (params = {}, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/ml_qc_rules/`, {
      params,
      signal,
    });
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const message = "Failed to fetch QC rules";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Create ML QC Rule
 * @param {{
 *  rule_type: string,
 *  rule_description: string,
 *  rule_details: { conditions: string[] },
 *  is_active: boolean
 * }} payload {
 *  rule_type: string,
 *  rule_description: string,
 *  rule_details: object,
 *  is_active: boolean
 * }
 */
export const createMlQcRule = async (payload) => {
  try {
    const response = await axios.post(`${API_URL}/ml_qc_rules/`, payload);
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Failed to create QC rule";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Update ML QC Rule
 * Note: only rule_id is mandatory in payload
 * @param {{
 *  rule_id: string,
 *  rule_type: string,
 *  rule_description: string,
 *  rule_details: { conditions: string[] },
 *  is_active: boolean
 * }} payload {
 *  rule_id: string,
 *  rule_type?: string,
 *  rule_description?: string,
 *  rule_details?: object,
 *  is_active?: boolean
 * }
 */
export const updateMlQcRule = async (payload) => {
  try {
    const response = await axios.patch(`${API_URL}/ml_qc_rules/`, payload);
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Failed to update QC rule";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Delete ML QC Rule
 * @param {*} ruleId string | payload { rule_id: string }
 */
export const deleteMlQcRule = async (ruleId) => {
  try {
    const payload = typeof ruleId === "string" ? { rule_id: ruleId } : ruleId;
    const response = await axios.delete(`${API_URL}/ml_qc_rules/`, { params: payload });
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Failed to delete QC rule";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Get ML QC Rules
 * @param {{ search?: string, rule_id?: string }} params { search?: string, rule_id?: string }
 * @param {*} signal AbortSignal
 */
export const getMlQcRulesRange = async (params = {}, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/customer_specific_ranges/`, {
      params,
      signal,
    });
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const message = "Failed to fetch QC rules";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Create ML QC Rule
 * @param {{
 *  rule_type: string,
 *  rule_description: string,
 *  rule_details: { conditions: string[] },
 *  is_active: boolean
 * }} payload {
 *  rule_type: string,
 *  rule_description: string,
 *  rule_details: object,
 *  is_active: boolean
 * }
 */
export const createMlQcRuleRange = async (payload) => {
  try {
    const response = await axios.post(`${API_URL}/customer_specific_ranges/`, payload);
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const data = e?.response?.data;
    const message = data?.message ?? "Failed to create QC rule";
    return {
      success: false,
      message,
      data,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Update ML QC Rule
 * Note: only rule_id is mandatory in payload
 * @param {{
 *  rule_id: string,
 *  rule_type: string,
 *  rule_description: string,
 *  rule_details: { conditions: string[] },
 *  is_active: boolean
 * }} payload {
 *  rule_id: string,
 *  rule_type?: string,
 *  rule_description?: string,
 *  rule_details?: object,
 *  is_active?: boolean
 * }
 */
export const updateMlQcRuleRange = async (payload) => {
  try {
    const response = await axios.patch(`${API_URL}/customer_specific_ranges/`, payload);
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Failed to update QC rule";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Delete ML QC Rule Range
 * @param {*} id string | payload { _id: string }
 */
export const deleteMlQcRuleRange = async (id) => {
  try {
    const payload = typeof id === "string" ? { _id: id } : id;
    const response = await axios.delete(`${API_URL}/customer_specific_ranges/`, { params: payload });
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Failed to delete QC rule range";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Get ML QC Rules
 * @param {{ search?: string, rule_id?: string }} params { search?: string, rule_id?: string }
 * @param {*} signal AbortSignal
 */
export const getMlQcRulesSpecific = async (params = {}, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/customer_specific_rules/`, {
      params,
      signal,
    });
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const message = "Failed to fetch QC rules";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Create ML QC Rule
 * @param {{
 *  rule_type: string,
 *  rule_description: string,
 *  rule_details: { conditions: string[] },
 *  is_active: boolean
 * }} payload {
 *  rule_type: string,
 *  rule_description: string,
 *  rule_details: object,
 *  is_active: boolean
 * }
 */
export const createMlQcRuleSpecific = async (payload) => {
  try {
    const response = await axios.post(`${API_URL}/customer_specific_rules/`, payload);
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const data = e?.response?.data;
    const message = data?.message ?? "Failed to create QC rule";
    return {
      success: false,
      message,
      data,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Update ML QC Rule
 * Note: only rule_id is mandatory in payload
 * @param {{
 *  rule_id: string,
 *  rule_type: string,
 *  rule_description: string,
 *  rule_details: { conditions: string[] },
 *  is_active: boolean
 * }} payload {
 *  rule_id: string,
 *  rule_type?: string,
 *  rule_description?: string,
 *  rule_details?: object,
 *  is_active?: boolean
 * }
 */
export const updateMlQcRuleSpecific = async (payload) => {
  try {
    const response = await axios.patch(`${API_URL}/customer_specific_rules/`, payload);
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Failed to update QC rule";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Delete ML QC Rule
 * @param {*} ruleId string | payload { rule_id: string }
 */
export const deleteMlQcRuleSpecific = async (ruleId) => {
  try {
    const payload = typeof ruleId === "string" ? { _id: ruleId } : ruleId;
    const response = await axios.delete(`${API_URL}/customer_specific_rules/`, { params: payload });
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Failed to delete QC rule";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Fetch the related tests for Micro category and MRL retest metadata.
 * MRL test objects may include:
 * - mrl_compounds: { compound, value, pesticide_instrument_id, instrument_name }[]
 * Missing means mrl_compounds as no instrument data.
 */
export const fetchRelatedTests = async (params) => {
  try {
    const response = await axios.get(`${API_URL}/getrelatedinfo/`, { params });
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    return { success: false, message: "Failed to load related workorder and tests" };
  }
};

/**
 * Data Ops RCA details — related workorders by genista_id + analysis.
 * Response shape matches getrelatedinfo/ (nested workorder/customer/sample/tests).
 * @param {{ genista_id: string, analysis: string }} params
 */
export const fetchRcaRelatedWorkorders = async (params, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/rca_related_workorders/`, {
      params,
      signal,
    });
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    return {
      success: false,
      message: e?.response?.data?.message || "Failed to load related workorder data",
      canceled: e?.code === "ERR_CANCELED",
    };
  }
};

/**
 * Fetches history details based on the provided date, history type and search
 * @param {object} payload - An object containing date, update_type, search
 * @param {AbortSignal} [signal=null] - An AbortSignal to cancel the request.
 * @return {object} An object containing the API response and a success flag.
 */
export const fetchHistory = async (payload, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/get_activity_log/`, { params: payload, signal });
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Fetches the ml image data for annotation
 * @param {object} payload - The parameters for the request (date, update_type, search)
 * @param {AbortSignal} [signal=null] - Optional AbortSignal to cancel the request.
 * @returns {Promise<object>} API response object containing success flag and data or error information.
 */
export const getMLCountQC = async (id, signal) => {
  if (!id) {
    return {
      success: false,
      message: "ML count id is required.",
      canceled: false,
    };
  }

  const params = { _id: id };
  try {
    const response = await axios.get(`${API_URL}/ml_counts_qc/`, { params, signal });
    return { success: true, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Submits (updates) ML Count QC annotation data for a given id.
 * @param {string} id - The id of the ML count record to update.
 * @param {object} updated_data - The updated annotation data to submit (e.g. colony_box_list, labels, etc.).
 * @param {AbortSignal} [signal=null] - Optional AbortSignal to cancel the request.
 * @returns {Promise<object>} API response object with success status and result or error info.
 */
export const submitMLCountQC = async (id, updated_data, signal = null) => {
  if (!id) {
    return {
      success: false,
      message: "ML count id is required.",
      canceled: false,
    };
  }

  const payload = { _id: id, updated_data };
  try {
    const response = await axios.post(`${API_URL}/ml_counts_qc/update/`, payload, { signal });
    return { success: true, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/*
 * GET with JSON body (per backend contract). Verify in dev that the server receives the body.
 * @param {{ test_name: string, result: string, conversion_unit: string }} payload
 */
export const convertUnitWrapper = async (payload) => {
  try {
    const response = await axios.post(`${API_URL}/unit_conversion_wrapper/`, {
      ...payload,
    });
    const data = await response.data;
    if (data.message.toLowerCase() === "error") {
      return {
        success: false, error: true, data, message: data.message,
      };
    }
    if (data.message.toLowerCase() === "success") {
      return {
        success: true, error: false, data, message: data.message,
      };
    }
    return {
      success: false, error: false, data, message: data.message,
    };
  } catch (e) {
    const message = e?.response?.data?.message || "Something went wrong.";
    return {
      success: false,
      message,
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Check test software QC spec
 * @param {*} params tis_pk: number
 * @returns {object} { success: boolean, result: { spec_blocked: boolean, reason: string } }
 */
export const checkTestSoftwareQCSpec = async (params) => {
  try {
    const response = await axios.get(`${API_URL}/check_test_spec/`, { params });
    return { success: true, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
    };
  }
};

/**
 * GET rca_test_samples/ — Add RCA eligible tests (Data Ops → RCA → RCA).
 * @param {{ source: string, date_range: string, search?: string, test_category_id?: number|string }} params
 */
export const fetchRcaTestSamples = async (params = {}, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/rca_test_samples/`, {
      params,
      signal,
    });
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Failed to fetch test samples";
    return {
      success: false,
      message,
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Fetch RCA tracking records
 * @param {{ tis_pk?: number,
 * search?: string,
 * status?: number|string,
 * filter?: string,
 * date_range?: string,
 * created_by?: number|string, updated_by?: number|string }} params
 * @param {AbortSignal|null} signal
 */
export const fetchRcaTracking = async (params = {}, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/rca_tracking/`, {
      params,
      signal,
    });
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Something went wrong.";
    return {
      success: false,
      message,
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Create RCA tracking record (Data Ops --> RCA --> RCA --> Add RCA).
 * @param {object} payload
 */
export const createRcaTracking = async (payload) => {
  try {
    const response = await axios.post(`${API_URL}/rca_tracking/`, payload);
    return { success: true, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Failed to create RCA",
    };
  }
};

/**
 * Patch RCA tracking (assignee, status, details).
 * @param {object} payload
 */
export const patchRcaTracking = async (payload) => {
  try {
    const response = await axios.patch(`${API_URL}/rca_tracking/`, payload);
    return { success: true, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Failed to update RCA",
    };
  }
};

/** POC name — inline assign-to dropdown */
export const updateRcaAssignedTo = patchRcaTracking;

/** Full RCA details submit from Data Ops --> RCA modals */
export const updateRcaTrackingDetails = patchRcaTracking;

/**
 * Fetch Retest reasons (reasons_category=retest).
 * @param {AbortSignal} [signal]
 */
export const getRetestReasons = async (signal) => {
  try {
    const response = await axios.get(`${API_URL}/reasons/`, {
      params: { reasons_category: "retest" },
      signal,
    });
    return { success: true, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Fetch workorder info logs for QC rule testing.
 * @param {{
 *  search?: string,
 *  test_category_id?: number|string,
 *  customer_pk?: number|string,
 *  date_range?: string,
 *  page?: number,
 *  page_size?: number,
 * }} params
 * @param {AbortSignal|null} signal
 */
export const getWoInfoLogs = async (params = {}, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/get_wo_info_logs/`, {
      params,
      signal,
    });
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Failed to fetch workorder info logs";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Run detect error rule check against selected tests. ML applies all rules whatever is valid
 * @param {{ tis_pks: number[] }} payload
 */
export const detectErrorRuleCheck = async (payload) => {
  try {
    const response = await axios.post(`${API_URL}/detect_error_rule_check/`, payload);
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    const message = e?.response?.data?.message ?? "Failed to check rule";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};
